import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Workspace } from '../types/schema';
import { useAuthContext } from './auth-context';
import { useWorkspaces } from '../hooks/use-workspace-query';
import { getLocalStorageCached, setLocalStorageCached, removeLocalStorageCached } from '../lib/storage-cache';

interface WorkspaceContextType {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  setCurrentWorkspace: (workspace: Workspace) => void;
  loading: boolean;
  error: string | null;
  refetchWorkspaces: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

const CURRENT_WORKSPACE_KEY = 'omni_current_workspace_id';

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuthContext();
  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | null>(null);
  const hasInitialized = useRef(false);

  // Use React Query for workspace fetching with aggressive caching
  // Only enable when user is authenticated (not just when auth is loaded)
  const isAuthenticated = !authLoading && user !== null;
  const { data: workspaces = [], isLoading, error: queryError, refetch } = useWorkspaces(isAuthenticated);

  const loading = authLoading || isLoading;
  const error = queryError ? (queryError.message || null) : null;

  // Set current workspace when workspaces load (only on initial load or when current becomes invalid)
  // Using primitive dependencies (rerender-dependencies best practice)
  const workspacesLength = workspaces?.length ?? 0;
  const currentWorkspaceId = currentWorkspace?.id;

  useEffect(() => {
    if (workspacesLength === 0) {
      setCurrentWorkspaceState(null);
      hasInitialized.current = false;
      return;
    }

    // If we already have a valid current workspace, don't change it
    if (currentWorkspaceId && workspaces.some(w => w.id === currentWorkspaceId)) {
      hasInitialized.current = true;
      return;
    }

    // Try to restore previously selected workspace from localStorage (using cache)
    const savedWorkspaceId = getLocalStorageCached(CURRENT_WORKSPACE_KEY);

    if (savedWorkspaceId) {
      const savedWorkspace = workspaces.find(w => w.id === parseInt(savedWorkspaceId));
      if (savedWorkspace) {
        setCurrentWorkspaceState(savedWorkspace);
        hasInitialized.current = true;
        return;
      } else {
        // Saved workspace no longer exists, clear it
        removeLocalStorageCached(CURRENT_WORKSPACE_KEY);
      }
    }

    // Default to first workspace only if we haven't initialized yet
    if (!hasInitialized.current) {
      setCurrentWorkspaceState(workspaces[0]);
      setLocalStorageCached(CURRENT_WORKSPACE_KEY, workspaces[0].id.toString());
      hasInitialized.current = true;
    }
  }, [workspacesLength, currentWorkspaceId, workspaces]);

  const setCurrentWorkspace = (workspace: Workspace) => {
    setCurrentWorkspaceState(workspace);
    setLocalStorageCached(CURRENT_WORKSPACE_KEY, workspace.id.toString());
  };

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        currentWorkspace,
        setCurrentWorkspace,
        loading,
        error,
        refetchWorkspaces: async () => { await refetch(); },
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
}
